import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #pendingStickyAdd = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#syncStickyVisibility, { passive: true, signal });
    window.addEventListener('resize', this.#syncStickyVisibility, { passive: true, signal });

    this.#setupIntersectionObserver();
    this.#syncStickyVisibility();

    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#pendingStickyAdd || !this.#targetAddToCartButton) return;

    const productForm = this.#targetAddToCartButton.form;
    if (!productForm || !productForm.checkValidity()) {
      productForm?.reportValidity();
      return;
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#pendingStickyAdd = true;

    // Submit the owning form explicitly. A synthetic button click can be
    // swallowed when the primary buy block is outside the viewport (the exact
    // state in which this sticky control is shown), leaving the customer with
    // no cart update or error. requestSubmit preserves native validation and
    // the original submitter while reliably reaching ProductFormComponent.
    if (typeof productForm.requestSubmit === 'function') {
      productForm.requestSubmit(this.#targetAddToCartButton);
    } else {
      this.#targetAddToCartButton.click();
    }
  };

  /** @param {MouseEvent} event */
  #handleStickyButtonClick = (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    if (!target?.closest('[ref="addToCartButton"]')) return;
    if (this.#pendingStickyAdd) {
      event.preventDefault();
      return;
    }

    const productFormComponent = this.#getProductForm();
    const productForm = productFormComponent?.querySelector('form');
    if (!productForm || !productForm.checkValidity()) {
      event.preventDefault();
      productForm?.reportValidity();
      return;
    }

    // Delegate directly to the existing ProductFormComponent. Some browsers do
    // not dispatch the form's delegated `on:submit` action when its submitter
    // lives outside that component (as this fixed sticky bar does). Calling the
    // component's public submit handler preserves its one AJAX/cart-drawer path
    // without cloning any cart logic here.
    event.preventDefault();
    this.#targetAddToCartButton = productFormComponent.querySelector('[ref="addToCartButton"]');
    if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#pendingStickyAdd = true;

    if (typeof productFormComponent.handleSubmit === 'function') {
      productFormComponent.handleSubmit(event);
      return;
    }

    this.#pendingStickyAdd = false;
    if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variant ? String(variant.available) : variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;

      const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
      if (variantTitleElement) {
        variantTitleElement.textContent = variant.title || variant.name || '';
      }
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles a successful cart update triggered by the sticky button.
   * @param {CustomEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    const eventData = event.detail?.data;
    const isCurrentProduct = String(eventData?.productId || '') === String(this.dataset.productId || '');

    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (!isCurrentProduct || eventData?.didError) return;

    this.#pendingStickyAdd = false;
    void this.#showAddSuccessFeedback();
  };

  /**
   * Resets the sticky state when the cart request fails.
   */
  #handleCartError = () => {
    this.#pendingStickyAdd = false;
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Shows the checkmark and fly-to-cart effect after a confirmed cart add.
   */
  #showAddSuccessFeedback = async () => {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const addToCartButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('button[ref="addToCartButton"]')
    );
    const productImage = /** @type {HTMLImageElement | null} */ (this.querySelector('[ref="productImage"]'));
    if (!addToCartButton) return;

    addToCartButton.dataset.added = 'true';
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    if (cartIcon && productImage) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
      await onAnimationEnd([addToCartButton, flyToCartElement]);
    }

    this.#resetTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    const stickyQuantity = this.querySelector('[data-sticky-quantity]');
    if (stickyQuantity instanceof HTMLInputElement) stickyQuantity.value = String(this.#currentQuantity);
    const stickyFormComponent = this.querySelector('.sticky-add-to-cart__form-component');
    if (stickyFormComponent instanceof HTMLElement) {
      stickyFormComponent.dataset.quantityDefault = String(this.#currentQuantity);
    }
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Keeps the sticky bar correct if an IntersectionObserver callback is missed
   * after a section morph, bfcache restore, or nested product-form upgrade.
   */
  #syncStickyVisibility = () => {
    const productForm = this.#getProductForm();
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!buyButtonsBlock || !footer) return;

    const buyButtonsRect = buyButtonsBlock.getBoundingClientRect();
    const footerRect = footer.getBoundingClientRect();
    const shouldShow = buyButtonsRect.bottom < 0 && footerRect.top > window.innerHeight;

    if (shouldShow && !this.#isStuck) {
      this.#hiddenByBottom = false;
      this.#showStickyBar();
    } else if (!shouldShow && this.#isStuck) {
      this.#hiddenByBottom = footerRect.top <= window.innerHeight;
      this.#hideStickyBar();
    }
  };

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} .buy-buttons-block product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const addToCartButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('button[ref="addToCartButton"]')
    );
    const quantityDisplay = /** @type {HTMLElement | null} */ (this.querySelector('[ref="quantityDisplay"]'));
    const quantityNumber = /** @type {HTMLElement | null} */ (this.querySelector('[ref="quantityNumber"]'));
    if (!addToCartButton || !quantityDisplay || !quantityNumber) return;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

// Resilient page-level visibility sync. This deliberately does not depend on
// the custom element lifecycle, so a nested component upgrade or bfcache restore
// cannot leave the fixed bar permanently off-screen.
const syncStickyAddToCartBars = () => {
  const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
  if (!footer) return;

  for (const sticky of document.querySelectorAll('sticky-add-to-cart')) {
    const section = sticky.closest('.shopify-section');
    const productId = sticky.getAttribute('data-product-id');
    const buyButtons = section?.querySelector(
      `.buy-buttons-block product-form-component[data-product-id="${productId}"]`
    );
    const bar = sticky.querySelector('.sticky-add-to-cart__bar');
    if (!buyButtons || !bar) continue;

    const shouldShow = buyButtons.getBoundingClientRect().bottom < 0 && footer.getBoundingClientRect().top > innerHeight;
    bar.setAttribute('data-stuck', String(shouldShow));
  }
};

window.addEventListener('scroll', syncStickyAddToCartBars, { passive: true });
window.addEventListener('resize', syncStickyAddToCartBars, { passive: true });
requestAnimationFrame(syncStickyAddToCartBars);

// Route sticky clicks through the already-tested primary product button. The
// sticky form lives outside the main ProductFormComponent, and Shopify's action
// delegation does not consistently capture that nested external submitter.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const stickyButton = /** @type {HTMLButtonElement | null} */ (
    target?.closest('sticky-add-to-cart button[ref="addToCartButton"]') ?? null
  );
  if (!stickyButton || stickyButton.disabled || stickyButton.getAttribute('aria-busy') === 'true') return;

  const sticky = stickyButton.closest('sticky-add-to-cart');
  const section = sticky?.closest('.shopify-section');
  const productId = sticky?.getAttribute('data-product-id');
  const primaryButton = /** @type {HTMLButtonElement | null} */ (
    section?.querySelector(
      `.buy-buttons-block product-form-component[data-product-id="${productId}"] button[name="add"]`
    ) ?? null
  );
  if (!primaryButton || primaryButton.disabled) return;

  event.preventDefault();
  stickyButton.setAttribute('aria-busy', 'true');
  primaryButton.dataset.puppet = 'true';
  primaryButton.click();
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const productId = String(event.detail?.data?.productId || '');
  if (!productId || event.detail?.data?.didError) return;

  const sticky = document.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`);
  const button = sticky?.querySelector('button[ref="addToCartButton"]');
  if (!(button instanceof HTMLButtonElement)) return;

  button.removeAttribute('aria-busy');
  button.dataset.added = 'true';
  setTimeout(() => button.removeAttribute('data-added'), 800);
});

document.addEventListener(ThemeEvents.cartError, () => {
  for (const button of document.querySelectorAll('sticky-add-to-cart button[aria-busy="true"]')) {
    button.removeAttribute('aria-busy');
  }
});
